/**
 * @tanstack/react-query hooks for the G15 Travel Pre-approvals module.
 *
 * Thin wrappers over lib/api/travel-preapprovals.ts so pages stay
 * declarative and caching/invalidation lives in one place.
 *
 * Query-key convention mirrors use-leave.ts / use-expenses.ts:
 *   ["travel-preapprovals", <area>, ...params]
 *
 * The list is role-FILTERED in SQL (employee=own, manager=own+reports,
 * HR=all) — the SQL scope is the real security boundary, not a query
 * param. The single-row GET endpoint EXISTS for this module (unlike
 * leave / expense which derive detail from the list) — the handler is
 * access-scoped server-side and 404s an out-of-scope id (no oracle).
 *
 * The shared QueryClient (components/providers.tsx) sets retry: 1,
 * refetchOnWindowFocus: false, staleTime: 30s — defaults are fine here.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { travelPreapprovalsApi } from "@/lib/api";
import { invalidateApprovalsSummary } from "@/lib/approvals-invalidate";
import type {
  ApproveTravelPreapprovalRequest,
  CreateTravelPreapprovalRequest,
  RejectTravelPreapprovalRequest,
  TravelPreapprovalStatus,
} from "@/lib/api/travel-preapprovals";

export const travelPreapprovalKeys = {
  all: ["travel-preapprovals"] as const,
  list: (status?: TravelPreapprovalStatus, excludeSelf?: boolean) =>
    [
      ...travelPreapprovalKeys.all,
      "list",
      status ?? "all",
      excludeSelf ? "excl-self" : "role",
    ] as const,
  one: (id: number) =>
    [...travelPreapprovalKeys.all, "one", id] as const,
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useTravelPreapprovals(
  status?: TravelPreapprovalStatus,
  opts?: { excludeSelf?: boolean },
) {
  return useQuery({
    queryKey: travelPreapprovalKeys.list(status, opts?.excludeSelf),
    queryFn: ({ signal }) =>
      travelPreapprovalsApi.listTravelPreapprovals(
        {
          ...(status ? { status } : {}),
          ...(opts?.excludeSelf ? { exclude_self: true } : {}),
        },
        signal,
      ),
  });
}

export function useTravelPreapproval(id: number, enabled = true) {
  return useQuery({
    queryKey: travelPreapprovalKeys.one(id),
    queryFn: ({ signal }) =>
      travelPreapprovalsApi.getTravelPreapproval(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
    // The detail endpoint 404s an out-of-scope or cross-tenant id (no
    // oracle). Don't retry — a hard 404 should surface immediately.
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/**
 * Any create/approve/reject/cancel changes which buckets a row lives in
 * (status filter changes) AND the dashboard widget's "my pending" count,
 * so flush the whole module root rather than guess which buckets moved.
 * Mirrors the expense / leave list-flush rule.
 */
function invalidateAllAfterMutation(
  qc: ReturnType<typeof useQueryClient>,
) {
  void qc.invalidateQueries({ queryKey: travelPreapprovalKeys.all });
  // The Inbox / tab badges count pending trips (phantom-count fix).
  invalidateApprovalsSummary(qc);
}

export function useCreateTravelPreapproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTravelPreapprovalRequest) =>
      travelPreapprovalsApi.createTravelPreapproval(body),
    onSuccess: () => invalidateAllAfterMutation(qc),
  });
}

export function useApproveTravelPreapproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: number;
      body: ApproveTravelPreapprovalRequest;
    }) =>
      travelPreapprovalsApi.approveTravelPreapproval(vars.id, vars.body),
    onSuccess: () => invalidateAllAfterMutation(qc),
  });
}

export function useRejectTravelPreapproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: number;
      body: RejectTravelPreapprovalRequest;
    }) =>
      travelPreapprovalsApi.rejectTravelPreapproval(vars.id, vars.body),
    onSuccess: () => invalidateAllAfterMutation(qc),
  });
}

export function useCancelTravelPreapproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      travelPreapprovalsApi.cancelTravelPreapproval(id),
    onSuccess: () => invalidateAllAfterMutation(qc),
  });
}
